import { PauseIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react'
import { Crown } from 'iconoir-react'
import type { ColumnDef } from '@tanstack/react-table'
import { useQuery } from 'urql'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { LIST_WORKFLOW_RUNS_QUERY } from 'app/lib/workflows/graphql'
import { cn } from 'app/lib/utils'

/** One row of the workflows list. Flattened for the table: the page already turns the engine's
 *  trigger blob into a readable phrase and its timestamps into a date. */
export type WorkflowRow = {
  id: string
  name: string
  status: `live` | `error` | `paused` | `off` | `draft`
  trigger: string
  /** The one location this workflow runs for (trackable-api #530). Absent only on a local draft
   *  that has not chosen one yet; the serializer blocks activation until it does. */
  locationId?: string
  /** The location's name, resolved by the page from the same locations query the builder's toolbar
   *  reads. Empty when the draft has no location or the id is not one the workspace can see. */
  location: string
  /** Formatted for display. */
  updated: string
  /** The same moment as a number, so sorting the column orders by date rather than alphabetically. */
  updatedAt: number
  /** Formatted for display. Empty when nothing recorded a creation time: an older local draft the
   *  server has never seen. */
  created: string
  /** Same pairing as `updatedAt`. 0 when unknown, which sorts it to the oldest end. */
  createdAt: number
  /** Local drafts that never synced can't be deleted server-side, so the row hides the action. */
  deletable: boolean
  /** The SERVER id runs are keyed by. For a locally-built workflow `id` is the local doc id, and a
   *  runs query on that silently returns zero forever, so the run cell must use this instead.
   *  Absent = never synced = genuinely can't have runs. */
  runsWorkflowId?: string
  /** Ours rather than the practice's: it writes a system pipeline the Action Center's own boards
   *  read. Only a super is ever handed a row with this set (the page filters the rest out), so the
   *  crown says why they can see something a practice cannot, not that they need permission. */
  system?: boolean
}

const STATUS_TONE: Record<WorkflowRow[`status`], string> = {
  live: `bg-inverse text-inverse`,
  error: `bg-danger-minimal text-danger-strong`,
  paused: `bg-warning-minimal text-warning-strong`,
  off: `bg-surface-muted text-subtle`,
  draft: `bg-surface-muted text-subtle`,
}

const runRecency = (iso: string | undefined): string => {
  if (!iso) return ``
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutes < 1) return `just now`
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`
  const days = Math.floor(minutes / (60 * 24))
  return days <= 30 ? `${days}d ago` : new Date(iso).toLocaleDateString(`en-US`, { month: `short`, day: `numeric` })
}

/** "12 · 2h ago": all-time run count plus how recently the newest one started (UX audit C9 — the
 *  list's only signal for "is this actually running"). One query per row buys exactness: `total`
 *  and the newest hit arrive together, and workflow lists are single-digit at launch scale. If
 *  lists outgrow that, the fix is a backend aggregate, not more rows of this. */
function WorkflowRunsCell({ workflowId }: { workflowId: string }) {
  const [result] = useQuery<{ workflowRuns: { hits: { startedAt?: string | null }[]; total: number } }>({
    query: LIST_WORKFLOW_RUNS_QUERY,
    variables: {
      search: `workflowId:${workflowId}`,
      pagination: { limit: 1 },
      sort: { field: `startedAt`, direction: `DESC` },
    },
  })
  if (!result.data) return <div className="px-2" />
  const total = result.data.workflowRuns.total
  const last = result.data.workflowRuns.hits[0]?.startedAt ?? undefined
  if (!total) return <div className="px-2 text-minimal">–</div>
  return (
    <div className="px-2 text-subtle">
      {total}
      {last ? <span className="text-minimal"> · {runRecency(last)}</span> : null}
    </div>
  )
}

const LOCATION_COLUMN: ColumnDef<WorkflowRow, any> = {
  id: `Location`,
  header: `Location`,
  // Sorts and displays the resolved name, so the column groups a template's deployments together
  // instead of ordering by opaque ids.
  // Blank for a draft that has not chosen a location yet, the same as Trigger for an empty phrase.
  accessorFn: (row) => row.location,
  size: 180,
  meta: { defaultVisible: true },
}

/** Same rule as the builder's toolbar picker: with one location there is nothing to tell apart, so
 *  the column is left out rather than filled with the same name down every row. */
export const workflowColumns = (
  onDelete: (id: string, name: string) => void,
  { showLocation }: { showLocation: boolean },
): ColumnDef<WorkflowRow, any>[] => [
  {
    id: `Name`,
    header: `Name`,
    accessorFn: (row) => row.name,
    size: 380,
    meta: {
      defaultVisible: true,
      // An anchor rather than plain text, so cmd-click opens a workflow in a new tab. Row click still
      // navigates (meta.onRowClick), but a list of links people cannot open in a tab is a small daily
      // annoyance for anyone comparing two workflows.
      ControlledComponent: (row?: WorkflowRow) => (
        <AnchorCell href={`/workflows/${row?.id ?? ``}`} shouldPreventDefault={false}>
          {/* One element, because AnchorCell hands its child straight to a Tooltip. The name keeps
              the truncation; the crown sits outside it so a long name never eats the badge. */}
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium text-default">{row?.name}</span>
            {/* Same crown, same amber as the super-only items in the nav (`NavItem.tsx`), because it
                carries the same meaning: this is Trackable's, and nobody else can see it. */}
            {row?.system && (
              <span title="System workflow, visible to Trackable staff only" className="flex shrink-0">
                <Crown width={14} height={14} className="text-amber-800" />
              </span>
            )}
          </span>
        </AnchorCell>
      ),
    },
  },
  {
    id: `Status`,
    header: `Status`,
    accessorFn: (row) => row.status,
    size: 120,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row?: WorkflowRow) => (
        <div className="flex items-center px-2">
          <span
            className={cn(
              `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize`,
              row ? STATUS_TONE[row.status] : ``,
            )}
            data-testid={row ? `workflow-status-${row.id}` : undefined}
          >
            {row?.status === `error` && <WarningIcon size={11} weight="bold" aria-hidden />}
            {row?.status === `paused` && <PauseIcon size={11} weight="bold" aria-hidden />}
            {row?.status}
          </span>
        </div>
      ),
    },
  },
  ...(showLocation ? [LOCATION_COLUMN] : []),
  {
    id: `Trigger`,
    header: `Trigger`,
    accessorFn: (row) => row.trigger,
    size: 280,
    meta: { defaultVisible: true },
  },
  {
    id: `Runs`,
    header: `Runs`,
    // Display-only: the data lives in per-row queries, so there is nothing in the row to sort on.
    enableSorting: false,
    size: 140,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row?: WorkflowRow) =>
        row?.runsWorkflowId ? (
          <WorkflowRunsCell workflowId={row.runsWorkflowId} />
        ) : (
          // Never synced: no server id, so no runs are possible, and a query would be noise.
          <div className="px-2 text-minimal">–</div>
        ),
    },
  },
  {
    id: `Created`,
    header: `Created`,
    accessorFn: (row) => row.createdAt,
    size: 140,
    meta: {
      defaultVisible: true,
      // A dash rather than an empty cell when unknown, matching Runs: blank reads as "not loaded".
      ControlledComponent: (row?: WorkflowRow) =>
        row?.created ? (
          <div className="truncate px-2 text-subtle">{row.created}</div>
        ) : (
          <div className="px-2 text-minimal">–</div>
        ),
    },
  },
  {
    id: `Last edited`,
    header: `Last edited`,
    // Sorts on the timestamp, renders the formatted date. Sorting the display string would put
    // "Apr 2" above "Aug 1" above "Feb 9".
    accessorFn: (row) => row.updatedAt,
    size: 160,
    // The last resizable column absorbs every pixel the window loses, so without a floor it drops
    // to the table's 60px default and the header and date spill under the delete button.
    minSize: 120,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row?: WorkflowRow) => <div className="truncate px-2 text-subtle">{row?.updated}</div>,
    },
  },
  {
    id: `Actions`,
    header: ``,
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 56,
    minSize: 56,
    maxSize: 56,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row?: WorkflowRow) => (
        <div className="flex items-center justify-center">
          {row?.deletable && (
            <button
              type="button"
              aria-label={`Delete ${row.name}`}
              title="Delete workflow"
              className="flex size-7 items-center justify-center rounded-md text-minimal hover:bg-red-50 hover:text-red-600"
              onClick={(event) => {
                // The row navigates; this button must not, or deleting would open the thing it deleted.
                event.stopPropagation()
                onDelete(row.id, row.name)
              }}
            >
              <TrashIcon size={15} weight="bold" />
            </button>
          )}
        </div>
      ),
    },
  },
]
